import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    LayoutDashboard,
    Search,
    Sparkles,
    Send,
    LineChart,
    Bot,
    Plug,
    Settings,
    Users,
    Wallet,
    Gift,
    Bot as BotIcon,
    BookOpen,
    ChevronsLeft,
    ChevronsRight,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { TEST_IDS } from "@/constants/testIds";

const items = (t) => [
    { key: "dashboard", to: "/dashboard", label: t.nav.dashboard, icon: LayoutDashboard, group: "main" },
    { key: "find", to: "/app/find", label: t.nav.find, icon: Search, group: "modules" },
    { key: "enrich", to: "/app/enrich", label: t.nav.enrich, icon: Sparkles, group: "modules" },
    { key: "engage", to: "/app/engage", label: t.nav.engage, icon: Send, group: "modules" },
    { key: "analyze", to: "/app/analyze", label: t.nav.analyze, icon: LineChart, group: "modules" },
    { key: "copilot", to: "/app/copilot", label: t.nav.copilot, icon: Bot, group: "main" },
    { key: "integrations", to: "/app/integrations", label: t.nav.integrations, icon: Plug, group: "bottom" },
    { key: "team", to: "/app/team", label: t.nav.team, icon: Users, group: "bottom" },
    { key: "billing", to: "/app/billing", label: t.nav.billing, icon: Wallet, group: "bottom" },
    { key: "referrals", to: "/app/referrals", label: "Referrals", icon: Gift, group: "bottom" },
    { key: "autopilot", to: "/app/autopilot", label: "Autopilote", icon: BotIcon, group: "bottom" },
    { key: "docs", to: "/app/docs", label: t.nav.docs || "Docs", icon: BookOpen, group: "bottom" },
    { key: "settings", to: "/app/settings", label: t.nav.settings, icon: Settings, group: "bottom" },
];

export default function Sidebar() {
    const [collapsed, setCollapsed] = useState(false);
    const { t } = useI18n();
    const all = items(t);
    const main = all.filter(i => i.group === "main");
    const mods = all.filter(i => i.group === "modules");
    const bottom = all.filter(i => i.group === "bottom");

    return (
        <motion.aside
            animate={{ width: collapsed ? 76 : 248 }}
            transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
            className="fixed left-0 top-0 bottom-0 z-40 border-r border-white/8 bg-brand-void/95 backdrop-blur-md flex flex-col"
        >
            <div className="h-16 flex items-center px-4 border-b border-white/8">
                <NavLink to="/dashboard" className="flex items-center gap-2 min-w-0">
                    <span className="w-9 h-9 rounded-lg btn-cta flex items-center justify-center shrink-0 text-white font-black text-sm">B</span>
                    <AnimatePresence>
                        {!collapsed && (
                            <motion.span
                                initial={{ opacity: 0, x: -6 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -6 }}
                                className="font-display font-black text-lg tracking-tight text-white truncate"
                            >
                                BOXEB2B
                            </motion.span>
                        )}
                    </AnimatePresence>
                </NavLink>
            </div>

            <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-6">
                <NavGroup items={main} collapsed={collapsed} />
                <NavGroup items={mods} label={!collapsed ? "Modules" : ""} collapsed={collapsed} />
                <NavGroup items={bottom} label={!collapsed ? "Workspace" : ""} collapsed={collapsed} />
            </nav>

            <button
                type="button"
                onClick={() => setCollapsed(!collapsed)}
                data-testid={TEST_IDS.app.sidebarCollapse}
                className="mx-3 mb-3 py-2 rounded-lg text-zinc-500 hover:text-white hover:bg-white/5 transition flex items-center justify-center border border-white/5"
            >
                {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
            </button>
        </motion.aside>
    );
}

function NavGroup({ items, label, collapsed }) {
    return (
        <div>
            {label && <div className="px-3 pb-2 text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">{label}</div>}
            <ul className="space-y-1">
                {items.map(({ key, to, label: name, icon: Icon }) => (
                    <li key={key}>
                        <NavLink
                            to={to}
                            end={to === "/dashboard"}
                            data-testid={TEST_IDS.app.sidebarItem(key)}
                            className={({ isActive }) =>
                                `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition relative ${
                                    isActive
                                        ? "text-white bg-white/5 nav-item-active"
                                        : "text-zinc-400 hover:text-white hover:bg-white/[0.04]"
                                }`
                            }
                        >
                            <Icon size={18} className="shrink-0" />
                            {!collapsed && <span className="truncate">{name}</span>}
                        </NavLink>
                    </li>
                ))}
            </ul>
        </div>
    );
}
